/* Signal: Scratch popup. Scratch the glare → email to Klaviyo → code applied to the cart. */
(() => {
  const STORE_KEY = 'sg_scratch_v1';
  const KLAVIYO_REVISION = '2026-07-15';
  const store = {
    get() { try { return JSON.parse(localStorage.getItem(STORE_KEY)) || {}; } catch (e) { return {}; } },
    set(v) { try { localStorage.setItem(STORE_KEY, JSON.stringify(v)); } catch (e) {} },
  };
  const designMode = !!(window.Shopify && window.Shopify.designMode);
  const forced = /[?&]scratch=1\b/.test(location.search);
  // ?scratch=1 turns on test mode for the rest of the visit, so the popup and bubble can be tried on every page before it is switched on
  const session = {
    get() { try { return sessionStorage.getItem('sg_scratch_test') === '1'; } catch (e) { return false; } },
    set() { try { sessionStorage.setItem('sg_scratch_test', '1'); } catch (e) {} },
  };
  if (forced) session.set();
  const testing = forced || session.get();
  // Views and scratches go to Klaviyo on one shared anonymous counter profile (visitors are unknown before signup),
  // so Viewed vs Signup in Klaviyo gives the popup's submission rate. Test mode and the theme editor are not counted.
  function track(key, name, props) {
    if (testing || !key || (window.Shopify && Shopify.designMode)) return;
    try {
      fetch(`https://a.klaviyo.com/client/events?company_id=${encodeURIComponent(key)}`, {
        method: 'POST', keepalive: true,
        headers: { 'content-type': 'application/vnd.api+json', revision: KLAVIYO_REVISION },
        body: JSON.stringify({ data: { type: 'event', attributes: {
          properties: Object.assign({ page: location.pathname, device: matchMedia('(pointer: fine)').matches ? 'desktop' : 'mobile' }, props || {}),
          metric: { data: { type: 'metric', attributes: { name } } },
          profile: { data: { type: 'profile', attributes: { anonymous_id: 'preysight-popup-counter' } } },
        } } }),
      }).catch(() => {});
    } catch (e) {}
  }

  function init(root) {
    if (!root || root.dataset.sgReady) return;
    root.dataset.sgReady = '1';
    let cfg;
    try { cfg = JSON.parse(root.querySelector('[data-sg-scratch-config]').textContent); } catch (e) { return; }

    const dialog = root.querySelector('[data-sg-scratch-dialog]');
    const card = root.querySelector('[data-sg-scratch-card]');
    const canvas = root.querySelector('[data-sg-scratch-canvas]');
    const form = root.querySelector('[data-sg-scratch-form]');
    const input = form.querySelector('input[type="email"]');
    const submit = form.querySelector('button[type="submit"]');
    const error = root.querySelector('[data-sg-scratch-error]');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const bubble = root.querySelector('[data-sg-scratch-bubble]');
    const DAY = 864e5, days = (cfg.days || 7) * DAY;
    const save = (patch) => { if (!designMode) store.set(Object.assign(store.get(), patch)); };
    let opened = false, painted = false, cleared = false, drawing = false, last = null, moves = 0;

    const setStep = (step) => { dialog.dataset.step = step; };

    /* ---------- glare layer ---------- */
    function paint() {
      const r = card.getBoundingClientRect();
      if (!r.width) return;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const w = r.width, h = r.height;
      canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.globalCompositeOperation = 'source-over';

      const g = ctx.createLinearGradient(0, 0, w, h);
      g.addColorStop(0, '#bcd0dc'); g.addColorStop(0.42, '#eef4f7'); g.addColorStop(0.5, '#ffffff');
      g.addColorStop(0.58, '#e3edf2'); g.addColorStop(1, '#9fb8c6');
      ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);

      // ripples
      ctx.lineWidth = 1;
      for (let y = 6; y < h; y += 11) {
        ctx.beginPath();
        for (let x = 0; x <= w; x += 6) ctx.lineTo(x, y + Math.sin((x + y * 4) / 17) * 2.2);
        ctx.strokeStyle = 'rgba(96, 130, 150, 0.22)'; ctx.stroke();
      }
      // sun glare streaks
      const streaks = [[0.12, 26, 0.55], [0.3, 10, 0.4], [0.62, 34, 0.5], [0.8, 12, 0.35]];
      streaks.forEach(([pos, width, alpha]) => {
        const x = pos * w;
        ctx.beginPath();
        ctx.moveTo(x, 0); ctx.lineTo(x + width, 0); ctx.lineTo(x + width - h * 0.5, h); ctx.lineTo(x - h * 0.5, h); ctx.closePath();
        ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`; ctx.fill();
      });
      // sparkles
      for (let i = 0; i < 26; i++) {
        const x = (Math.sin(i * 12.9898) * 43758.5453 % 1 + 1) % 1 * w;
        const y = (Math.sin(i * 78.233) * 12543.123 % 1 + 1) % 1 * h;
        ctx.beginPath(); ctx.arc(x, y, 1 + (i % 3) * 0.6, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)'; ctx.fill();
      }

      ctx.fillStyle = '#0C0C0C'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      try { ctx.fontStretch = 'expanded'; } catch (e) {}
      ctx.font = `900 ${w < 360 ? 19 : 22}px Archivo, "Arial Black", sans-serif`;
      ctx.fillText(String(cfg.scratchLabel || '').toUpperCase(), w / 2, h / 2 - 10);
      try { ctx.fontStretch = 'normal'; } catch (e) {}
      ctx.font = '600 12px Archivo, Arial, sans-serif';
      ctx.fillStyle = 'rgba(12, 12, 12, 0.6)';
      ctx.fillText(cfg.hint || '', w / 2, h / 2 + 18);
      painted = true;
    }

    /* ---------- scratching ---------- */
    const pos = (e) => { const r = canvas.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
    function stroke(a, b) {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 42; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
    }
    function clearedRatio() {
      const { width, height } = canvas;
      const data = ctx.getImageData(0, 0, width, height).data;
      let clear = 0, total = 0;
      for (let i = 3; i < data.length; i += 4 * 24) { total++; if (data[i] < 64) clear++; }
      return total ? clear / total : 0;
    }
    function reveal() {
      if (cleared) return;
      cleared = true;
      card.classList.add('is-clear');
      save({ r: 1 });
      track(cfg.key, 'Scratch Popup Scratched');
      setTimeout(() => {
        setStep('form');
        if (matchMedia('(pointer: fine)').matches) input.focus({ preventScroll: true });
      }, 550);
    }
    canvas.addEventListener('pointerdown', (e) => {
      if (cleared) return;
      drawing = true; last = pos(e);
      try { canvas.setPointerCapture(e.pointerId); } catch (err) {}
      stroke(last, { x: last.x + 0.1, y: last.y + 0.1 });
    });
    canvas.addEventListener('pointermove', (e) => {
      if (!drawing || cleared) return;
      const p = pos(e); stroke(last, p); last = p;
      if (++moves % 8 === 0 && clearedRatio() > 0.45) reveal();
    });
    const end = () => { if (!drawing) return; drawing = false; if (!cleared && clearedRatio() > 0.45) reveal(); };
    canvas.addEventListener('pointerup', end);
    canvas.addEventListener('pointercancel', end);
    root.querySelector('[data-sg-scratch-skip]').addEventListener('click', reveal);

    /* ---------- open / close ---------- */
    function open(trigger) {
      if (dialog.open) return;
      track(cfg.key, 'Scratch Popup Viewed', { trigger: typeof trigger === 'string' ? trigger : 'bubble' });
      opened = true;
      hideBubble();
      if (!cleared && store.get().r) { cleared = true; card.classList.add('is-clear'); setStep('form'); }
      if (typeof dialog.showModal === 'function') dialog.showModal(); else dialog.setAttribute('open', '');
      const go = () => requestAnimationFrame(() => { if (!painted && !cleared) paint(); });
      if (document.fonts && document.fonts.load) document.fonts.load('900 22px Archivo').then(go, go); else go();
    }
    function close(remember = true) {
      if (dialog.open) dialog.close();
      if (remember && dialog.dataset.step !== 'done') { save({ s: 'dismissed', t: Date.now() }); showBubble(); }
    }

    /* ---------- reopen bubble ---------- */
    function showBubble() {
      if (!bubble || !cfg.bubble) return;
      const st = store.get();
      if (!designMode && st.bh && Date.now() - st.bh < days) return;
      bubble.hidden = false;
      void bubble.offsetWidth; // commit the hidden state so the pop-in transition runs
      bubble.classList.add('is-in');
    }
    function hideBubble() {
      if (!bubble) return;
      bubble.classList.remove('is-in');
      bubble.hidden = true;
    }
    if (bubble) {
      bubble.querySelector('[data-sg-scratch-reopen]').addEventListener('click', open);
      bubble.querySelector('[data-sg-scratch-bubble-close]').addEventListener('click', () => { save({ bh: Date.now() }); hideBubble(); });
    }
    root.querySelector('[data-sg-scratch-close]').addEventListener('click', () => close());
    dialog.addEventListener('cancel', (e) => { e.preventDefault(); close(); });
    dialog.addEventListener('click', (e) => { if (e.target === dialog) close(); });
    window.addEventListener('resize', () => { if (dialog.open && !cleared && !drawing && moves === 0) paint(); });

    /* ---------- submit to Klaviyo ---------- */
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const email = input.value.trim();
      error.hidden = true;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
        error.textContent = 'Please enter a valid email address.'; error.hidden = false; input.focus(); return;
      }
      if (!cfg.key || !cfg.list) {
        error.textContent = 'Not connected to Klaviyo yet. Add the public key and list ID in the theme editor.'; error.hidden = false; return;
      }
      submit.disabled = true; submit.textContent = 'Unlocking…';
      try {
        const res = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(cfg.key)}`, {
          method: 'POST',
          headers: { 'content-type': 'application/vnd.api+json', revision: KLAVIYO_REVISION },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'Scratch popup',
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email,
                      properties: { signup_source: 'scratch_popup', scratch_prize: cfg.prize, scratch_code: cfg.code },
                      subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                    },
                  },
                },
              },
              relationships: { list: { data: { type: 'list', id: cfg.list } } },
            },
          }),
        });
        if (!res.ok) throw new Error(`Klaviyo ${res.status}`);
        // the subscription call can drop custom properties, so tag the profile and log a countable event separately
        const kHeaders = { 'content-type': 'application/vnd.api+json', revision: KLAVIYO_REVISION };
        const kBase = `https://a.klaviyo.com/client`;
        const kQuery = `?company_id=${encodeURIComponent(cfg.key)}`;
        const props = { signup_source: 'scratch_popup', scratch_prize: cfg.prize, scratch_code: cfg.code };
        fetch(`${kBase}/profiles${kQuery}`, {
          method: 'POST', headers: kHeaders,
          body: JSON.stringify({ data: { type: 'profile', attributes: { email, properties: props } } }),
        }).catch(() => {});
        fetch(`${kBase}/events${kQuery}`, {
          method: 'POST', headers: kHeaders,
          body: JSON.stringify({ data: { type: 'event', attributes: {
            properties: props,
            metric: { data: { type: 'metric', attributes: { name: 'Scratch Popup Signup' } } },
            profile: { data: { type: 'profile', attributes: { email } } },
          } } }),
        }).catch(() => {});
        // let Klaviyo's onsite script (if installed) recognise this browser, so Viewed Product / browse abandonment can follow
        try {
          if (window.klaviyo && typeof window.klaviyo.identify === 'function') window.klaviyo.identify({ email });
          else { window._learnq = window._learnq || []; window._learnq.push(['identify', { $email: email }]); }
        } catch (err) {}
        success();
      } catch (err) {
        console.warn('[sg-scratch]', err);
        error.textContent = 'Something went wrong. Please try again.'; error.hidden = false;
        submit.disabled = false; submit.textContent = submit.dataset.label;
      }
    });

    function success() {
      setStep('done');
      hideBubble();
      if (!designMode) store.set({ s: 'subscribed', t: Date.now() });
      if (cfg.code) {
        const code = encodeURIComponent(cfg.code);
        fetch(`${cfg.root.replace(/\/$/, '')}/discount/${code}`, { credentials: 'same-origin' }).catch(() => {});
        const shop = root.querySelector('[data-sg-scratch-shop]');
        shop.href = `${cfg.root.replace(/\/$/, '')}/discount/${code}?redirect=${encodeURIComponent(cfg.shopPath)}`;
      }
      document.dispatchEvent(new CustomEvent('sg:scratch:subscribed', { detail: { prize: cfg.prize } }));
    }

    const copyBtn = root.querySelector('[data-sg-scratch-copy]');
    copyBtn.addEventListener('click', () => {
      const done = () => { copyBtn.textContent = 'Copied'; setTimeout(() => { copyBtn.textContent = 'Copy'; }, 1800); };
      if (navigator.clipboard) navigator.clipboard.writeText(cfg.code).then(done, done); else done();
    });

    /* ---------- triggers ---------- */
    if (designMode) {
      document.addEventListener('shopify:section:select', (e) => { if (e.target.contains(root)) open(); });
      document.addEventListener('shopify:section:deselect', (e) => { if (e.target.contains(root)) close(false); });
      return;
    }
    if (forced) { open(); return; }

    const st = store.get();
    if ((!cfg.enabled || !cfg.ready) && !testing) return;
    if (st.s === 'subscribed') return;
    if (st.s === 'dismissed' && Date.now() - (st.t || 0) < days) { showBubble(); return; }
    if (cfg.isCart) return;

    const busy = () => !!document.querySelector('dialog[open]') || document.body.classList.contains('overflow-hidden') ||
      document.documentElement.classList.contains('sg-drawer-open') || !!document.querySelector('cart-drawer.is-open, .menu-drawer-container[open], details[open] > .menu-drawer');
    const tryOpen = (trigger) => { if (opened) return; if (busy()) { setTimeout(() => tryOpen(trigger), 4000); return; } open(trigger); };
    setTimeout(() => tryOpen('timer'), Math.max(0, cfg.delay || 0) * 1000);
    if (cfg.exit && matchMedia('(pointer: fine)').matches) {
      document.addEventListener('mouseout', function onOut(e) {
        if (e.relatedTarget || e.clientY > 0) return;
        document.removeEventListener('mouseout', onOut);
        tryOpen('exit');
      });
    }
  }

  const boot = () => document.querySelectorAll('[data-sg-scratch]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', (e) => { const r = e.target.querySelector('[data-sg-scratch]'); if (r) init(r); });
})();
